import { makeT } from "../i18n";

const t = makeT("footer");

/*
 * Cobuntu Footer Widget v1
 *
 * Drop-in branded community footer for white-label landing pages.
 *
 * Why this exists:
 *   The community-app renders a per-community footer (components/layout/
 *   Footer.tsx): a brand block (icon + name + socials), an "Explore"
 *   column that mirrors the header nav 1:1, and a "Powered by Cobuntu"
 *   bottom bar — all themed from the community's theme config. A landing
 *   served from the customer's own apex (Lovable / Squarespace / a
 *   bespoke Next app like dddimo.com) never renders that footer, so its
 *   native pages (/, /marketplace, …) lose the community footer entirely.
 *   This widget bridges that gap: it fetches the same storefront config,
 *   resolves the same nav with the same visibility rules, and renders a
 *   faithful, theme-matched copy into the customer's page.
 *
 * Mount point:
 *   <div data-cobuntu-footer data-tag="<community-tag>"></div>
 *
 *   Drop the div wherever the footer should sit (typically last in the
 *   page). The widget renders the full <footer> as a child after it
 *   hydrates; it owns everything inside the mount.
 *
 * Theming:
 *   By default the footer themes itself from the community's themeConfig
 *   (bg / text / brand / link colors + fonts), returned by the by-domain
 *   bootstrap — so it matches the community-app footer without the
 *   landing having to declare any CSS variables. Any of those can be
 *   overridden by setting the matching `--cobuntu-footer-*` CSS variable
 *   on (or above) the mount, for landings that want pixel parity with a
 *   bespoke palette.
 *
 * Auth gating: matches the nav widget / DesktopHeader exactly — module
 *   entries are gated by their page's visibility (enabled + public, or
 *   members-only when the viewer is an accepted member); custom NavLink
 *   entries are gated by their own `visibleWhen`.
 */

// ─── Constants ────────────────────────────────────────────────────

const COOKIE_NAME = "cobuntu_auth";
const MOUNT_SELECTOR = "[data-cobuntu-footer]";
const MOUNTED_ATTR = "data-cobuntu-footer-mounted";
const STYLE_ID = "cobuntu-footer-widget-styles";
const POLL_INTERVAL_MS = 60_000;
const EXP_SKEW_MS = 30_000;

import { authedFetch } from "../api/authedFetch";
import { getCachedSession, primeSession } from "../shared/session";

declare const __WIDGET_API_URL__: string;

/**
 * Absolute API base for UNAUTHENTICATED calls only — community lookup by
 * domain, publishable key. Those endpoints carry no session, so cross-origin
 * is fine and the widget may run on a customer landing where they are not
 * proxied.
 *
 * AUTHED calls must NOT use this. They go through `authedFetch`, which is
 * same-origin by design: the cobuntu_auth cookie is host-scoped, so an authed
 * cross-origin call arrives anonymous and every widget renders a guest. That
 * was a live outage — see api/authedFetch.ts.
 */
const API_URL: string =
  typeof __WIDGET_API_URL__ !== "undefined" ? __WIDGET_API_URL__ : "https://api.cobuntu.com";


// ─── Types — mirror community-app/lib/types.ts ────────────────────

type NavVisibility = "always" | "authenticated" | "approvedMember";

interface NavLink {
  href: string;
  label: string;
  visibleWhen?: NavVisibility;
  target?: string;
  external?: boolean;
}

interface NavGroup {
  label: string;
  children?: NavLink[];
  visibleWhen?: NavVisibility;
}

type NavEntry = string | NavLink | NavGroup;

interface ThemeConfig {
  bgColor?: string;
  textColor?: string;
  brandColor?: string;
  linkColor?: string;
  headingFont?: string;
  bodyFont?: string;
  containerMaxWidth?: string;
}

interface PageSetting {
  pageKey?: string;
  enabled?: boolean;
  visibility?: string | null; // null | "PUBLIC" | "MEMBERS_ONLY"
}

interface CommunityResponse {
  name?: string;
  iconUrl?: string | null;
  socials?: string[] | Record<string, string> | null;
  pageSettings?: PageSetting[] | null;
  themeConfig?: ThemeConfig | null;
  storefrontConfig?: {
    nav?: { items?: NavEntry[] };
    navLabels?: Partial<Record<string, string>> | null;
  } | null;
}

interface MembershipsResponse {
  memberships?: Array<{
    communityTag?: string;
    status?: string;
    platforms?: { cobuntu?: { status?: string } };
  }>;
}

// Resolved community data the footer renders from.
interface FooterData {
  name: string;
  iconUrl: string | null;
  socials: string[];
  theme: ThemeConfig;
  links: ResolvedLink[]; // Explore column, already gated + ordered
}

interface ResolvedLink {
  href: string;
  label: string;
  external: boolean;
}

// ─── Module-key → route + page-key maps ───────────────────────────
//
// Mirrors lib/footer-nav.ts NAV_BY_KEY and the header's nav resolution.
// "home" is intentionally absent — the footer prepends its own
// Home → /hub. Hrefs are RELATIVE: on a proxied apex they resolve to
// the native page where the customer overrides it (e.g. /marketplace)
// and proxy-forward to the community-app elsewhere (/events, /members),
// exactly as the community-app footer's links do.

const NAV_BY_KEY: Record<string, string> = {
  feed: "/feed",
  marketplace: "/marketplace",
  events: "/events",
  members: "/members",
  blog: "/blog",
  atlas: "/atlas",
};

// module key → pageSettings pageKey (UPPERCASE), for the enabled +
// visibility gate.
const PAGE_KEY_BY_NAV: Record<string, string> = {
  feed: "FEED",
  marketplace: "MARKETPLACE",
  events: "EVENTS",
  members: "MEMBERS",
  blog: "BLOG",
  atlas: "ATLAS",
};

// Default English labels — overridable per community via
// storefrontConfig.navLabels[key]. Mirrors the i18n `nav` namespace
// defaults the community-app footer reads (English fallback is an
// accepted white-label default until shared i18n lands).
const DEFAULT_LABELS: Record<string, string> = {
  home: "Home",
  feed: "Feed",
  marketplace: "Marketplace",
  events: "Events",
  members: "Members",
  blog: "Blog",
  atlas: "Atlas",
};

// ─── Per-mount state ──────────────────────────────────────────────

interface HostState {
  tag: string;
  data: FooterData | null;
  isAcceptedMember: boolean;
}

const HOSTS = new WeakMap<HTMLElement, HostState>();

// ─── Cookie / auth check (mirrors nav.ts) ─────────────────────────

/**
 * Is this visitor signed in? (app platform S3.)
 *
 * Reads the SERVER-resolved session instead of decoding the auth cookie here.
 * This file read `document.cookie` INLINE rather than through the shared
 * helper, which is why it survived the first sweep — and would have kept
 * decoding a cookie that S5 makes unreadable, concluding "guest" for every
 * signed-in member without throwing.
 *
 * Synchronous on purpose: mountAll() defers until the session is known.
 */
function isAuthenticated(): boolean {
  return getCachedSession()?.authenticated === true;
}

async function fetchIsAcceptedMember(tag: string): Promise<boolean> {
  if (!isAuthenticated()) return false;
  try {
    const res = await authedFetch(`/api/users/me/memberships/cobuntu`);
    if (!res.ok) return false;
    const body = (await res.json()) as MembershipsResponse;
    const m = (body.memberships ?? []).find((row) => row.communityTag === tag);
    if (!m) return false;
    const status = m.platforms?.cobuntu?.status ?? m.status ?? null;
    return status === "ACCEPTED";
  } catch {
    return false;
  }
}

// ─── Gating ───────────────────────────────────────────────────────

function passesGate(
  visibleWhen: NavVisibility | undefined,
  flags: { isAuthenticated: boolean; isAcceptedMember: boolean },
): boolean {
  if (!visibleWhen || visibleWhen === "always") return true;
  if (visibleWhen === "authenticated") return flags.isAuthenticated;
  if (visibleWhen === "approvedMember") return flags.isAuthenticated && flags.isAcceptedMember;
  return true;
}

// A module page shows in the footer when it's enabled and either public
// or members-only-and-the-viewer-is-a-member. Mirrors
// isPageVisibleInNav (lib/page-visibility.ts).
function moduleVisible(
  navKey: string,
  pageSettings: PageSetting[],
  isAcceptedMember: boolean,
): boolean {
  const pageKey = PAGE_KEY_BY_NAV[navKey];
  if (!pageKey) return true; // unknown key — don't gate
  const setting = pageSettings.find((p) => p.pageKey === pageKey);
  if (!setting) return true; // no row — default visible
  if (setting.enabled === false) return false;
  const v = setting.visibility;
  if (v === "MEMBERS_ONLY") return isAcceptedMember;
  return true; // null / "PUBLIC"
}

// ─── Nav resolution — mirrors lib/footer-nav.ts resolveFooterNav ──

function resolveLinks(
  body: CommunityResponse,
  flags: { isAuthenticated: boolean; isAcceptedMember: boolean },
): ResolvedLink[] {
  const raw = body.storefrontConfig?.nav?.items ?? [];
  const navLabels = body.storefrontConfig?.navLabels ?? null;
  const pageSettings = body.pageSettings ?? [];
  const labelForKey = (key: string): string =>
    navLabels?.[key] || DEFAULT_LABELS[key] || key;

  const out: ResolvedLink[] = [];
  for (const entry of raw) {
    // module-key string
    if (typeof entry === "string") {
      if (entry === "home") continue; // footer owns Home → /hub
      const href = NAV_BY_KEY[entry];
      if (!href) continue;
      if (!moduleVisible(entry, pageSettings, flags.isAcceptedMember)) continue;
      out.push({ href, label: labelForKey(entry), external: false });
      continue;
    }
    // NavGroup (dropdown) — flatten children
    if ("children" in entry && entry.children) {
      const group = entry as NavGroup;
      if (!passesGate(group.visibleWhen, flags)) continue;
      for (const child of group.children ?? []) {
        if (!child.href || !child.label) continue;
        if (!passesGate(child.visibleWhen, flags)) continue;
        out.push({ href: child.href, label: child.label, external: !!child.external });
      }
      continue;
    }
    // custom NavLink — included as-is, subject to its own gate
    const link = entry as NavLink;
    if (!link.href || !link.label) continue;
    if (!passesGate(link.visibleWhen, flags)) continue;
    out.push({ href: link.href, label: link.label, external: !!link.external });
  }
  return out;
}

// ─── Config fetch ─────────────────────────────────────────────────

async function fetchData(
  flags: { isAuthenticated: boolean; isAcceptedMember: boolean },
): Promise<FooterData | null> {
  try {
    const host = typeof window === "undefined" ? "" : window.location.host;
    if (!host) return null;
    const res = await fetch(
      `${API_URL}/api/communities/by-domain/${encodeURIComponent(host)}`,
      { credentials: "include" },
    );
    if (!res.ok) return null;
    const body = (await res.json()) as CommunityResponse;

    const rawSocials = body.socials;
    const socials: string[] = Array.isArray(rawSocials)
      ? rawSocials.filter(Boolean)
      : rawSocials && typeof rawSocials === "object"
        ? (Object.values(rawSocials).filter(Boolean) as string[])
        : [];

    return {
      name: body.name || "",
      iconUrl: body.iconUrl || null,
      socials,
      theme: body.themeConfig || {},
      links: resolveLinks(body, flags),
    };
  } catch {
    return null;
  }
}

// ─── Render ───────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

// A neutral "globe/link" glyph stands in for per-network social icons —
// v1 keeps the icon set out of the bundle; the link is labelled by its
// hostname for accessibility.
const SOCIAL_GLYPH =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';

function socialLabel(url: string): string {
  try {
    const u = url.startsWith("http") ? url : `https://${url}`;
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function renderInto(host: HTMLElement): void {
  const state = HOSTS.get(host);
  if (!state || !state.data) return;
  const d = state.data;
  const year = new Date().getFullYear();

  const brandIcon = d.iconUrl
    ? `<img src="${esc(d.iconUrl)}" alt="" class="cobuntu-footer-brand-icon" />`
    : "";

  const socialsHtml = d.socials.length
    ? `<div class="cobuntu-footer-socials">${d.socials
        .map((url) => {
          const href = url.startsWith("http") ? url : `https://${url}`;
          const label = socialLabel(url);
          return `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer" aria-label="${esc(label)}" title="${esc(label)}" class="cobuntu-footer-social">${SOCIAL_GLYPH}</a>`;
        })
        .join("")}</div>`
    : "";

  // Explore column: Home → /hub, then the resolved community nav.
  const exploreItems = [
    { href: "/hub", label: DEFAULT_LABELS.home, external: false },
    ...d.links,
  ];
  const linksHtml = exploreItems
    .map(
      (l) =>
        `<li><a href="${esc(l.href)}" class="cobuntu-footer-link"${l.external ? ' target="_blank" rel="noopener noreferrer"' : ""}>${esc(l.label)}</a></li>`,
    )
    .join("");

  host.innerHTML = `
<footer class="cobuntu-footer" part="footer">
  <div class="cobuntu-footer-inner">
    <div class="cobuntu-footer-grid">
      <div class="cobuntu-footer-brand">
        <a href="/hub" class="cobuntu-footer-brand-link" aria-label="${esc(d.name)}">
          ${brandIcon}
          <span class="cobuntu-footer-brand-name">${esc(d.name)}</span>
        </a>
        ${socialsHtml}
      </div>
      <div class="cobuntu-footer-col">
        <h3 class="cobuntu-footer-col-h">${esc(t("explore", "Explore"))}</h3>
        <ul class="cobuntu-footer-links">${linksHtml}</ul>
      </div>
    </div>
    <div class="cobuntu-footer-bottom">
      <span>© ${year} ${esc(d.name)}</span>
      <span>${esc(t("poweredBy", "Powered by"))} <a href="https://cobuntu.com" target="_blank" rel="noopener noreferrer" class="cobuntu-footer-cobuntu">Cobuntu</a></span>
    </div>
  </div>
</footer>`;

  applyTheme(host, d.theme);
}

// Theme is applied as inline CSS custom properties on the mount so the
// stylesheet (which reads --cobuntu-footer-*) resolves to the
// community's palette. Any property already set on/above the mount by
// the landing wins (we only set the var when it isn't already defined),
// so a landing can override individual tokens for pixel parity.
function applyTheme(host: HTMLElement, theme: ThemeConfig): void {
  const vars: Record<string, string | undefined> = {
    "--cobuntu-footer-bg": theme.bgColor,
    "--cobuntu-footer-text": theme.textColor,
    "--cobuntu-footer-brand": theme.brandColor,
    "--cobuntu-footer-link": theme.linkColor || theme.brandColor,
    "--cobuntu-footer-heading-font": theme.headingFont,
    "--cobuntu-footer-body-font": theme.bodyFont,
    "--cobuntu-footer-max-width": theme.containerMaxWidth,
  };
  for (const [name, value] of Object.entries(vars)) {
    if (!value) continue;
    if (host.style.getPropertyValue(name)) continue; // landing override wins
    host.style.setProperty(name, value);
  }
}

// ─── Per-host bootstrap ──────────────────────────────────────────

async function bootstrapHost(host: HTMLElement): Promise<void> {
  if (host.getAttribute(MOUNTED_ATTR) === "1") return;
  host.setAttribute(MOUNTED_ATTR, "1");

  const tag = host.getAttribute("data-tag") || "";
  if (!tag) return; // no tag → nothing to render; leave layout untouched

  const state: HostState = { tag, data: null, isAcceptedMember: false };
  HOSTS.set(host, state);

  const member = await fetchIsAcceptedMember(tag);
  state.isAcceptedMember = member;
  const flags = { isAuthenticated: isAuthenticated(), isAcceptedMember: member };
  state.data = await fetchData(flags);
  renderInto(host);
}

// ─── Mount + DOM watch ────────────────────────────────────────────

function mountAll(): void {
  // Do not paint before the session is known (S3). Hosts are marked MOUNTED
  // and skipped thereafter, so an early render STICKS. primeSession() is
  // cached, so re-entry from the MutationObserver costs nothing.
  if (getCachedSession() === null) {
    void primeSession().then(mountAll);
    return;
  }
  document
    .querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}:not([${MOUNTED_ATTR}])`)
    .forEach((host) => { void bootstrapHost(host); });
}

let observer: MutationObserver | null = null;
function watchDom(): void {
  if (observer) return;
  observer = new MutationObserver(mountAll);
  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Poll for auth/membership change (mirrors nav.ts) ─────────────

let pollTimer: ReturnType<typeof setInterval> | null = null;
function startPolling(): void {
  if (pollTimer) return;
  const tick = async () => {
    if (document.visibilityState !== "visible") return;
    const mounts = document.querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}[${MOUNTED_ATTR}]`);
    for (const host of Array.from(mounts)) {
      const state = HOSTS.get(host);
      if (!state) continue;
      const member = await fetchIsAcceptedMember(state.tag);
      const flags = { isAuthenticated: isAuthenticated(), isAcceptedMember: member };
      const data = await fetchData(flags);
      const changed =
        member !== state.isAcceptedMember ||
        JSON.stringify(data?.links) !== JSON.stringify(state.data?.links);
      if (changed) {
        state.isAcceptedMember = member;
        state.data = data;
        renderInto(host);
      }
    }
  };
  pollTimer = setInterval(() => { void tick(); }, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void tick();
  });
}

// ─── Styles ───────────────────────────────────────────────────────
//
// Self-contained, theme-driven via --cobuntu-footer-* vars (set from
// themeConfig in applyTheme, overridable by the landing). Mirrors the
// community-app footer's multi-column → stacked-on-mobile layout.

const CSS = `
.cobuntu-footer {
  margin-top: auto;
  border-top: 1px solid color-mix(in srgb, currentColor 15%, transparent);
  background: var(--cobuntu-footer-bg, #ffffff);
  color: var(--cobuntu-footer-text, #1a1a1a);
  font-family: var(--cobuntu-footer-body-font, system-ui, -apple-system, sans-serif);
  padding: 56px 0 32px;
}
.cobuntu-footer-inner {
  max-width: var(--cobuntu-footer-max-width, 80rem);
  margin: 0 auto;
  padding: 0 24px;
}
.cobuntu-footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 48px;
  padding-bottom: 40px;
  border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent);
}
.cobuntu-footer-brand-link {
  display: inline-flex; align-items: center; gap: 10px;
  text-decoration: none; color: inherit; width: fit-content;
}
.cobuntu-footer-brand-icon {
  width: 30px; height: 30px; border-radius: 8px; object-fit: cover;
  border: 1px solid color-mix(in srgb, currentColor 18%, transparent); flex-shrink: 0;
}
.cobuntu-footer-brand-name {
  font-family: var(--cobuntu-footer-heading-font, var(--cobuntu-footer-body-font, sans-serif));
  font-size: 1.15rem; font-weight: 600; line-height: 1.2;
}
.cobuntu-footer-socials { display: flex; align-items: center; gap: 8px; margin-top: 20px; }
.cobuntu-footer-social {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 9999px;
  border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  background: transparent; color: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}
.cobuntu-footer-social:hover {
  background: color-mix(in srgb, currentColor 10%, transparent);
  border-color: color-mix(in srgb, currentColor 35%, transparent);
  transform: translateY(-1px);
}
.cobuntu-footer-col-h {
  margin: 0 0 16px;
  font-size: 0.72rem; font-weight: 600; letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--cobuntu-footer-brand, #18181b);
  opacity: 0.85;
}
.cobuntu-footer-links { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 11px; }
.cobuntu-footer-link {
  color: var(--cobuntu-footer-text, #1a1a1a);
  opacity: 0.62; text-decoration: none; font-size: 0.875rem;
  transition: color 0.15s ease, opacity 0.15s ease; width: fit-content;
}
.cobuntu-footer-link:hover { opacity: 1; color: var(--cobuntu-footer-link, var(--cobuntu-footer-brand, #18181b)); }
.cobuntu-footer-bottom {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 12px;
  padding-top: 24px; font-size: 0.8rem; opacity: 0.6;
}
.cobuntu-footer-cobuntu { color: inherit; text-decoration: none; font-weight: 600; }
.cobuntu-footer-cobuntu:hover { color: var(--cobuntu-footer-link, var(--cobuntu-footer-brand, #18181b)); opacity: 1; }
@media (max-width: 720px) {
  .cobuntu-footer-grid { grid-template-columns: 1fr; gap: 32px; }
  .cobuntu-footer-bottom { flex-direction: column; align-items: flex-start; }
}
`;

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─── Boot ─────────────────────────────────────────────────────────

function init(): void {
  injectStyles();
  mountAll();
  watchDom();
  startPolling();
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}

// ─── Public API ───────────────────────────────────────────────────

declare global {
  interface Window {
    CobuntuFooter?: { refresh: () => Promise<void> };
  }
}

if (typeof window !== "undefined") {
  window.CobuntuFooter = {
    refresh: async () => {
      const mounts = document.querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}[${MOUNTED_ATTR}]`);
      for (const host of Array.from(mounts)) {
        const state = HOSTS.get(host);
        if (!state) continue;
        const member = await fetchIsAcceptedMember(state.tag);
        state.isAcceptedMember = member;
        state.data = await fetchData({ isAuthenticated: isAuthenticated(), isAcceptedMember: member });
        renderInto(host);
      }
    },
  };
}

// ─── Test exports ─────────────────────────────────────────────────

export const __test = {
  COOKIE_NAME,
  MOUNT_SELECTOR,
  NAV_BY_KEY,
  PAGE_KEY_BY_NAV,
  passesGate,
  moduleVisible,
  resolveLinks,
  socialLabel,
  esc,
};
